import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useRef } from "react";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { tokens } from "../../../theme";
import StepHeader from "./StepHeader";

interface FormData {
  name: string;
  surname: string;
  phone: string;
  email: string;
  businessName: string;
  organisationCategory: string;
  agreeTerms: boolean;
  idPhoto: File | null;
}

interface FormErrors {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  businessName?: string;
  organisationCategory?: string;
  agreeTerms?: string;
  idPhoto?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  formData: FormData;
  errors: FormErrors;
  onChange: (
    field: keyof FormData,
    value: string | boolean | File | null,
  ) => void;
  onNext: () => void;
  isSubmitting?: boolean;
}

const categories = [
  { label: "Funeral Parlour", value: "funeral_parlor" },
  {
    label: "Church / Religious Organisation",
    value: "church/religious_organization",
  },
  { label: "Community Organisation", value: "community_organization" },
  { label: "Insurance Company", value: "insurance_company" },
  { label: "Individual Consultant", value: "individual_consultant" },
  { label: "Other", value: "other" },
];

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5, color: "text.primary" }}
    >
      {children}
      {required && (
        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>
          *
        </Box>
      )}
    </Typography>
  );
}

function HintText({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontSize: "11px",
        mt: 0.4,
        display: "block",
      }}
    >
      {children}
    </Typography>
  );
}

export default function PersonalInfoForm({
  open,
  onClose,
  formData,
  errors,
  onChange,
  onNext,
  isSubmitting,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isIndividual =
    formData.organisationCategory === "individual_consultant";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) onChange("idPhoto", file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) onChange("idPhoto", file);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    onChange("phone", value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          width: "480px",
          maxHeight: "85vh",
          overflowY: "hidden",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "#fff",
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader>
        <div>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#fff"
            lineHeight={1.3}
          >
            Join the Muzukuru Referral Program
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.88)",
              mt: 0.5,
              display: "block",
              lineHeight: 1.4,
            }}
          >
            Earn commission by referring clients for funeral live streaming. It
            takes less than a minute to get started.
          </Typography>
        </div>
      </StepHeader>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Name & Surname */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <FieldLabel required>Name</FieldLabel>
              <TextField
                fullWidth
                placeholder="Enter your first name"
                size="small"
                value={formData.name}
                onChange={(e) => onChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                sx={fieldSx}
              />
            </Box>
            <Box>
              <FieldLabel required>Surname</FieldLabel>
              <TextField
                fullWidth
                placeholder="Enter your last name"
                size="small"
                value={formData.surname}
                onChange={(e) => onChange("surname", e.target.value)}
                error={!!errors.surname}
                helperText={errors.surname}
                sx={fieldSx}
              />
            </Box>
          </Box>

          {/* Phone — flag + country code picker */}
          <Box>
            <FieldLabel required>Phone Number</FieldLabel>
            <MuiTelInput
              fullWidth
              size="small"
              value={formData.phone}
              onChange={handlePhoneChange}
              defaultCountry="ZW"
              variant="outlined"
              error={!!errors.phone}
              helperText={errors.phone}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  height: "40px",
                  fontSize: "0.875rem",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  padding: "0 8px",
                  height: "40px",
                },
              }}
            />
            {!errors.phone && (
              <HintText>For updates and communication</HintText>
            )}
          </Box>

          {/* Email */}
          <Box>
            <FieldLabel>Email</FieldLabel>
            <TextField
              fullWidth
              placeholder="you@example.com"
              size="small"
              value={formData.email}
              onChange={(e) => onChange("email", e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              sx={fieldSx}
            />
            {!errors.email && (
              <HintText>
                We will send a verification code to this email
              </HintText>
            )}
          </Box>

          {/* Business Name */}
          <Box>
            <FieldLabel required>Business / Consultant Name</FieldLabel>
            <TextField
              fullWidth
              placeholder="Your funeral home or brand"
              size="small"
              value={formData.businessName}
              onChange={(e) => onChange("businessName", e.target.value)}
              error={!!errors.businessName}
              helperText={errors.businessName}
              sx={fieldSx}
            />
            {!errors.businessName && (
              <HintText>This helps us track your referrals correctly</HintText>
            )}
          </Box>

          {/* Organisation Category */}
          <Box>
            <FieldLabel>Organisation Category</FieldLabel>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.organisationCategory}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  "& fieldset": { border: "none" },
                },
              }}
            >
              <Select
                displayEmpty
                value={formData.organisationCategory}
                onChange={(e) =>
                  onChange("organisationCategory", e.target.value)
                }
                renderValue={(selected) => {
                  if (!selected)
                    return (
                      <Typography sx={{ color: "#A0938D" }}>
                        select your organisation
                      </Typography>
                    );
                  return (
                    categories.find((c) => c.value === selected)?.label ||
                    selected
                  );
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.organisationCategory && (
                <FormHelperText>{errors.organisationCategory}</FormHelperText>
              )}
            </FormControl>
            {!errors.organisationCategory && (
              <HintText>Select the option that best describes you</HintText>
            )}
          </Box>

          {/* ID Photo Upload — only for Individual Consultant */}
          {isIndividual && (
            <Box>
              <FieldLabel>ID Document</FieldLabel>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />

              {!formData.idPhoto ? (
                <Box
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${errors.idPhoto ? "#d32f2f" : "#C4A99E"}`,
                    borderRadius: "16px",
                    backgroundColor: colors.primary[800],
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "#724E41" },
                  }}
                >
                  <UploadFileIcon sx={{ fontSize: 32, color: "#C4A99E" }} />
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Click to upload or drag & drop
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    JPG, PNG, WEBP or PDF — max 5MB
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #C4A99E",
                    borderRadius: "16px",
                    backgroundColor: colors.primary[800],
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <InsertDriveFileIcon
                    sx={{ color: "#724E41", fontSize: 28, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formData.idPhoto.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                    >
                      {formatFileSize(formData.idPhoto.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => onChange("idPhoto", null)}
                    sx={{ color: "#d32f2f", flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {errors.idPhoto ? (
                <FormHelperText error>{errors.idPhoto}</FormHelperText>
              ) : (
                <HintText>
                  A clear photo or scan of your national ID or passport
                </HintText>
              )}
            </Box>
          )}

          {/* Terms */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeTerms}
                  onChange={(e) => onChange("agreeTerms", e.target.checked)}
                  sx={{
                    color: "#8D6E63",
                    "&.Mui-checked": { color: "#6D4C41" },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: "#5D4037", fontSize: "0.82rem" }}
                >
                  I agree to the partnership terms and privacy policy
                </Typography>
              }
            />
            {errors.agreeTerms && (
              <FormHelperText error sx={{ ml: 4 }}>
                {errors.agreeTerms}
              </FormHelperText>
            )}
          </Box>

          {/* Submit */}
          <Box>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onNext}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#724E41",
                py: 1.4,
                borderRadius: "24px",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4E342E" },
                "&.Mui-disabled": {
                  backgroundColor: "#c4a99e",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting ? "Please wait..." : "Continue"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
